import React from "react";

import * as R from "ramda";

import {
  compose,
  withProps,
  withHandlers,
  lifecycle,
  branch,
  renderComponent,
  withState,
  defaultProps,
} from "recompose";

import camelCase from "camel-case";

import { Box, Flex, Text, Link } from "src/components";

import sdk from "src/sdk";

import { DynamicForm } from "src/components/Form";

import { defaultPrevented } from "src/utils";
import { deviceOOBFields } from "src/banking/authentication/sharedFields";

import { useBankingContent } from "src/banking/BankingContentContext";
import { Body } from "src/components/Content";

import { BoxWithKeyboardPadding } from "src/banking/components";

const usernamePattern = /[a-zA-Z][a-zA-Z0-9.\-_@]{5,50}/;

const buildKey = keyName => R.pipe(R.toString, R.concat(`${keyName}`));

const questionKey = buildKey("challengeQuestions");
const answerKey = buildKey("challengeAnswers");

const initialValues = {
  recaptcha: "string",
};

const getAvailableQuestions = ({
  index,
  values,
  challengeQuestionsRequired,
  allOptions,
}) => {
  const usedQuestions = R.pipe(
    vals =>
      R.times(questionIndex => {
        const question =
          index === questionIndex
            ? undefined
            : values[questionKey(questionIndex)];
        return question;
      }, challengeQuestionsRequired),
    R.reject(R.isNil)
  )(values);

  const availableQuestions = R.reject(option => {
    return R.contains(option.value, usedQuestions);
  }, allOptions);

  return availableQuestions;
};

const buildChallengeQuestionField = ({
  challengeQuestionsRequired,
  questionOptions,
}) => index => props => {
  const availableOptions = getAvailableQuestions({
    index,
    values: props.values,
    challengeQuestionsRequired: challengeQuestionsRequired,
    allOptions: questionOptions,
  });

  return {
    type: "section",
    style: {
      mb: 3,
    },
    fields: [
      {
        key: questionKey(index),
        label: `Question #${index + 1}`,
        required: true,
        type: "dropdown",
        inputProps: {
          wrap: true,
          options: availableOptions,
        },
      },
      {
        key: answerKey(index),
        label: "Answer",
        required: true,
        inputProps: {
          autoComplete: "off",
        },
      },
    ],
  };
};

const fields = ({
  questionOptions,
  challengeQuestionsRequired,
  enrollmentState,
}) => ({ t, values, ...others }) => {
  const invalidUsernames = R.map(R.toLower, [
    enrollmentState.firstName,
    enrollmentState.lastName,
    enrollmentState.accountNumber,
  ]);

  return [
    {
      type: "section",
      title: t("olb.security.verificationCodeFormTitle"),
      fields: deviceOOBFields({ t }),
      renderBelowSectionTitle: () => (
        <Box>
          <Text fontSize={2} align="left" mb={2}>
            {t("olb.security.verifyCodeMessage")}
          </Text>
          <Link
            to="#"
            id={"enrollment-resendCode"}
            color="green"
            mb={2}
            align="left"
            fontSize={2}
            onClick={defaultPrevented(() => {
              sdk.resendSecurityCode(enrollmentState.resendSecurityCodePath);
            })}
          >
            {t("olb.security.resendCode")}
          </Link>
        </Box>
      ),
    },
    {
      type: "section",
      title: t("olb.security.createUsernameAndPassword"),
      fields: [
        {
          key: "username",
          label: "Username",
          required: true,
          inputProps: {
            autoComplete: "off",
          },
          constraints: {
            format: {
              pattern: usernamePattern,
              message: `^Please make sure your username is between 6 and 50 characters long and starts with a letter.`,
            },
            custom: {
              isValid: value =>
                !R.contains(value.toLowerCase(), invalidUsernames),
              message: `^Your username can't be the same as your first name, last name, or account number.`,
            },
          },
        },
        {
          key: "password",
          label: "Password",
          helpText: t("olb.password.requirements"),
          inputProps: {
            type: "password",
          },
          constraints: {
            // Note this is the inverse of the usual usage of `equality`.
            equality: {
              attribute: "username",
              message: `^Your password can't be the same as your username.`,
              comparator: (v1, v2) => v1 !== v2,
            },
          },
          required: true,
        },
        {
          key: "passwordConfirmation",
          label: "Password Confirmation",
          required: true,
          constraints: {
            equality: {
              attribute: "password",
              message: `^${t("olb.enrollment.passwordConfirmationError")}`,
            },
          },
          inputProps: {
            type: "password",
          },
        },
      ],
    },
    {
      type: "section",
      title: "Security Questions",
      fields: R.pipe(
        R.times(
          buildChallengeQuestionField({
            challengeQuestionsRequired,
            questionOptions,
          })
        ),
        R.flatten
      )(challengeQuestionsRequired),
    },
  ];
};

const buildQuestionOption = question => ({
  value: question.challengeId,
  label: question.challengeQuestion,
});

const getChallengeAnswers = data => index => ({
  key: R.prop(questionKey(index), data),
  value: R.prop(answerKey(index), data),
});

const enhance = compose(
  withState("questionOptions", "setQuestionOptions"),
  defaultProps({
    challengeQuestionsRequired: 3,
  }),
  withProps(props => ({
    initialValues: {
      ...initialValues,
      ...props.defaultValues,
    },
  })),
  withHandlers({
    onSubmit: props => async data => {
      const challengeAnswers = R.times(
        getChallengeAnswers(data),
        props.challengeQuestionsRequired
      );

      const verifyResult = await sdk.createCredentials(
        {
          credentials: {
            username: data.username,
            password: data.password,
            challengeAnswers,
            challengeCode: data.code,
            deviceNickname: data.nickname,
          },
        },
        {
          path: props.enrollmentState.createCredentialsPath,
        }
      );

      const nextState = R.path(["data", "state"], verifyResult);

      if (nextState) {
        props.setEnrollmentState({
          ...props.enrollmentState,
          key: camelCase(nextState),
        });
      }
    },
    onLoad: props => async () => {
      const challengeQuestions = await sdk.getChallengeQuestions();

      props.setQuestionOptions(R.map(buildQuestionOption, challengeQuestions));
    },
  }),
  lifecycle({
    componentWillMount() {
      this.props.onLoad();
    },
  }),
  branch(R.complement(R.prop("questionOptions")), renderComponent(Flex))
);

const EnrollmentSecurityForm = props => {
  const pageData = useBankingContent();
  return (
    <BoxWithKeyboardPadding>
      {pageData.enrollmentPageSecurityContent ? (
        <Body
          padded={false}
          content={pageData.enrollmentPageSecurityContent.content}
        />
      ) : null}
      <DynamicForm
        formKey="enrollment"
        fields={fields(props)}
        needsCaptcha={false}
        initialValues={props.initialValues}
        {...props}
      />
    </BoxWithKeyboardPadding>
  );
};

export default enhance(EnrollmentSecurityForm);



// WEBPACK FOOTER //
// ./src/banking/authentication/Enroll/steps/Security.js